import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Modal,
  Checkbox,
  DatePicker,
  Button,
  Progress,
  List,
  Alert,
} from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import moment from 'moment'
import { createXeroBill, fetchXeroProducts } from 'src/features/ReceiptingSlice'
import { fetchXeroUnsentOrders } from 'src/features/XeroIntegrationSlice'
import {
  hasSupplierXeroDefaults,
  planXeroExportJobs,
} from 'src/utils/xeroAutoSendBill'
import {
  resolveXeroOrganization,
  resolveXeroOrganizationId,
} from 'src/utils/xeroOrganization'
import { API } from 'src/utils/interceptor'
import { getLinkedInvoicesForReceiptingOrder } from 'src/utils/reconciliationHealth'

const { RangePicker } = DatePicker

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

const SendUnsentXeroModal = ({ visible, onClose, venueId, onComplete }) => {
  const dispatch = useDispatch()
  const venue = useSelector(state => state.venue?.venue)
  const uploadedInvoices = useSelector(
    state => state.newInvoices?.uploadedInvoices || [],
  )
  const destinationOrg = resolveXeroOrganization(venue?.xeroConnection)
  const abortRef = useRef(false)

  const [range, setRange] = useState([moment().subtract(90, 'days'), moment()])
  const [includeUnreconciled, setIncludeUnreconciled] = useState(false)
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [orders, setOrders] = useState([])
  const [rowState, setRowState] = useState({})
  const [destinationName, setDestinationName] = useState(
    destinationOrg?.name || '',
  )

  const loadOrders = useCallback(async () => {
    if (!venueId || !visible) return
    setLoading(true)
    const from = range?.[0]?.format('YYYY-MM-DD')
    const to = range?.[1]?.format('YYYY-MM-DD')
    const data = await dispatch(
      fetchXeroUnsentOrders(venueId, {
        from,
        to,
        includeUnreconciled,
      }),
    )
    setOrders(data?.orders || [])
    setDestinationName(data?.destinationOrg?.name || destinationOrg?.name || '')
    setRowState({})
    setLoading(false)
  }, [
    venueId,
    visible,
    range,
    includeUnreconciled,
    dispatch,
    destinationOrg?.name,
  ])

  useEffect(() => {
    if (visible) {
      abortRef.current = false
      loadOrders()
    } else {
      // Modal closed — stop any in-flight batch before the next order
      abortRef.current = true
      setSending(false)
    }
  }, [visible, loadOrders])

  const progress = useMemo(() => {
    const keys = Object.keys(rowState)
    if (!orders.length) return 0
    const done = keys.filter(
      k => rowState[k]?.status === 'success' || rowState[k]?.status === 'fail',
    ).length
    return Math.round((done / orders.length) * 100)
  }, [rowState, orders.length])

  const failedOrders = useMemo(
    () =>
      orders.filter(o => {
        const key = o.purchaseOrderNumber
        return rowState[key]?.status === 'fail'
      }),
    [orders, rowState],
  )

  const handleClose = () => {
    abortRef.current = true
    setSending(false)
    onClose()
  }

  const sendOne = async (order, { forceSend = false } = {}) => {
    if (abortRef.current) return { success: false, aborted: true }

    const key = order.purchaseOrderNumber
    if (order.xeroInvoiceId) {
      setRowState(prev => ({
        ...prev,
        [key]: { status: 'success', message: 'Already in Xero' },
      }))
      return { success: true, skipped: true }
    }

    const venueSupplier = order.venueSupplier || {
      supplierTitle: order?.supplier?.title,
      defaultAccount: '',
      defaultContact: '',
    }

    // Do not attempt send without mapped account + contact (unless Force)
    if (!forceSend && !hasSupplierXeroDefaults(venueSupplier)) {
      setRowState(prev => ({
        ...prev,
        [key]: {
          status: 'fail',
          message:
            'Skipped — map Xero account and contact for this supplier under Your Suppliers.',
        },
      }))
      return { success: false, skipped: true }
    }

    setRowState(prev => ({
      ...prev,
      [key]: {
        status: 'sending',
        message: forceSend ? 'Force sending…' : 'Sending…',
      },
    }))

    try {
      if (abortRef.current) return { success: false, aborted: true }

      const supplierId = order?.supplier?._id
      const products = await dispatch(
        fetchXeroProducts(venueId, supplierId, order.purchaseOrderNumber),
      )
      if (abortRef.current) return { success: false, aborted: true }
      if (!products || products === false) {
        throw new Error('Could not load receipting lines for this order')
      }
      const organizationId =
        resolveXeroOrganizationId(venue?.xeroConnection) || destinationOrg?.id
      let xeroAccounts = []
      try {
        const accountsResponse = await API.get(
          'api2',
          `venue/${venueId}/xero/accounts?organizationId=${organizationId}`,
        )
        xeroAccounts = accountsResponse?.data || []
      } catch (accountsError) {
        console.warn(
          'Could not load Xero accounts for product nominal codes',
          accountsError,
        )
      }
      const financeDocuments = getLinkedInvoicesForReceiptingOrder(
        order,
        uploadedInvoices,
      )
      const plan = planXeroExportJobs({
        financeDocuments,
        venue,
        order: {
          ...order,
          orderedDate: order.orderedDate,
          orderIds: order.orderIds,
          purchaseOrderNumber: order.purchaseOrderNumber,
        },
        venueSupplier,
        receiptingProducts: products,
        forceSend,
        xeroAccounts,
      })

      let anySuccess = false
      let lastError = null
      if (plan.mode === 'documents' && plan.jobs.length === 0) {
        setRowState(prev => ({
          ...prev,
          [key]: {
            status: 'success',
            message: 'Already in Xero',
          },
        }))
        return { success: true }
      }
      let latestBillXeroId = null
      for (const job of plan.jobs) {
        if (abortRef.current) return { success: false, aborted: true }
        if (
          job.documentType === 'creditNote' &&
          job.billData &&
          latestBillXeroId &&
          !job.billData.allocateToXeroInvoiceId
        ) {
          job.billData.allocateToXeroInvoiceId = latestBillXeroId
        }
        try {
          const result = await dispatch(
            createXeroBill(
              job.billData,
              [],
              venueId,
              organizationId,
              order.orderIds,
              job.selectedInvoiceId || order.attachedInvoiceId || '',
              {
                redirectOnSuccess: false,
                notifyOnSuccess: false,
                notifyOnError: false,
                rejectOnError: true,
                forceSend,
              },
            ),
          )
          if (result?.success && (result?.xeroInvoiceId || result?.alreadySent)) {
            anySuccess = true
            if (job.documentType === 'invoice' && result?.xeroInvoiceId) {
              latestBillXeroId = result.xeroInvoiceId
            }
          } else {
            throw new Error(result?.message || 'Send failed')
          }
        } catch (err) {
          lastError = err
        }
      }
      if (abortRef.current) return { success: false, aborted: true }
      if (anySuccess && !lastError) {
        setRowState(prev => ({
          ...prev,
          [key]: {
            status: 'success',
            message: plan.mode === 'documents' ? 'Sent (bill + CN)' : 'Sent',
          },
        }))
        return { success: true }
      }
      if (anySuccess && lastError) {
        setRowState(prev => ({
          ...prev,
          [key]: {
            status: 'fail',
            message: lastError?.message || 'Partial send failed',
          },
        }))
        return { success: false, error: lastError }
      }
      throw new Error(lastError?.message || 'Send failed')
    } catch (err) {
      if (abortRef.current) return { success: false, aborted: true }
      setRowState(prev => ({
        ...prev,
        [key]: {
          status: 'fail',
          message: err?.message || 'Failed',
        },
      }))
      return { success: false, error: err }
    }
  }

  const runBatch = async (list, { forceSend = false } = {}) => {
    abortRef.current = false
    setSending(true)
    for (const order of list) {
      if (abortRef.current) break
      if (order.xeroInvoiceId) continue
      const result = await sendOne(order, { forceSend })
      if (abortRef.current || result?.aborted) break
      await sleep(400)
    }
    const wasAborted = abortRef.current
    setSending(false)
    if (!wasAborted && onComplete) onComplete()
  }

  const handleSendAll = () => runBatch(orders, { forceSend: false })
  const handleRetryFailed = () => runBatch(failedOrders, { forceSend: false })
  const handleForceRemaining = () =>
    runBatch(
      failedOrders.length
        ? failedOrders
        : orders.filter(
            o => rowState[o.purchaseOrderNumber]?.status !== 'success',
          ),
      { forceSend: true },
    )

  return (
    <Modal
      title='Send unsent orders to Xero'
      visible={visible}
      onCancel={handleClose}
      width={720}
      footer={[
        <Button key='close' onClick={handleClose}>
          {sending ? 'Stop & close' : 'Close'}
        </Button>,
        <Button
          key='retry'
          onClick={handleRetryFailed}
          disabled={sending || !failedOrders.length}
        >
          Retry failed
        </Button>,
        <Button
          key='force'
          onClick={handleForceRemaining}
          disabled={sending || (!failedOrders.length && !orders.length)}
        >
          Force remaining
        </Button>,
        <Button
          key='send'
          type='primary'
          loading={sending}
          onClick={handleSendAll}
          disabled={loading || !orders.length || sending}
        >
          Send all
        </Button>,
      ]}
    >
      <Alert
        type='info'
        showIcon
        style={{ marginBottom: 12 }}
        message={`${orders.length} order(s) not in Xero`}
        description={`Destination: ${
          destinationName || '—'
        }. Bills are created as AUTHORISED ACCPAY.`}
      />

      <Filters>
        <RangePicker
          value={range}
          onChange={value => setRange(value)}
          allowClear={false}
        />
        <Checkbox
          checked={includeUnreconciled}
          onChange={e => setIncludeUnreconciled(e.target.checked)}
        >
          Include orders without invoice / not reconciled
        </Checkbox>
        <Button onClick={loadOrders} loading={loading} disabled={sending}>
          Refresh list
        </Button>
      </Filters>

      {(sending || Object.keys(rowState).length > 0) && (
        <Progress percent={progress} style={{ marginBottom: 12 }} />
      )}

      <List
        loading={loading}
        size='small'
        bordered
        dataSource={orders}
        locale={{ emptyText: 'No unsent orders in this range' }}
        renderItem={item => {
          const state = rowState[item.purchaseOrderNumber]
          return (
            <List.Item>
              <Row>
                <OrderInfo>
                  <PoNumber>{item.purchaseOrderNumber}</PoNumber>
                  <Meta>
                    {item.supplier?.title || '—'} · {item.status}
                    {item.attachedInvoiceId
                      ? ` · invoice ${
                          item.attachedInvoiceNumber || item.attachedInvoiceId
                        }`
                      : ' · no invoice'}
                  </Meta>
                </OrderInfo>
                <Status $status={state?.status}>
                  {state?.message || 'Pending'}
                </Status>
              </Row>
            </List.Item>
          )
        }}
      />
    </Modal>
  )
}

export default SendUnsentXeroModal

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-bottom: 16px;
`

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  gap: 16px;
`

const OrderInfo = styled.div`
  flex: 1 1 40%;
  min-width: 160px;
  max-width: 55%;
`

const PoNumber = styled.strong`
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Meta = styled.div`
  font-size: 12px;
  color: #666;
  word-break: break-word;
`

const Status = styled.span`
  flex: 1 1 45%;
  min-width: 140px;
  max-width: 55%;
  font-size: 12px;
  line-height: 1.4;
  text-align: right;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  color: ${p =>
    p.$status === 'success'
      ? '#389e0d'
      : p.$status === 'fail'
      ? '#cf1322'
      : p.$status === 'sending'
      ? '#096dd9'
      : '#8c8c8c'};
`
